import { reactive, ref } from 'vue'
import type { Ref } from 'vue'
import { journalsApi } from '@/views/apps/journals/api/journalsApi'
import { logJournalHttpError } from '@/views/apps/journals/composables/useErrorHandler'
import { normalizeFilterChoice } from '@/views/apps/journals/utils/normalizeFilterChoice'
import {
  getFormKey,
  isMultiSelect,
  needsChoices,
} from '@/views/apps/journals/utils/attributeHelpers'
import type {
  AttributeValueType,
  JournalAttribute,
} from '@/views/apps/journals/types/journal.types'
import type { IFilterChoiceItem } from '@/modules/universalStats/types/IFilterChoiceItem'
import type { IFilterStore } from '@/types/IFilterStore'
import type { ITreeSelectStore } from '@/types/ITreeSelectStore'

export interface SelectItem {
  id: string
  name: string
  children?: SelectItem[]
}

interface UseEntryChoicesLoaderParams {
  getJournalId: () => number | null
  journalAttributes: Ref<JournalAttribute[]>
  entryForm: Ref<Record<string, AttributeValueType>>
  errors: Ref<Record<string, string>>
  entryLoadedChoiceLabels: Ref<Record<string, string>>
  treeSelectedLabels: Ref<Record<string, Record<string | number, string>>>
}

const choiceToSelectItem = (item: IFilterChoiceItem): SelectItem => ({
  id: String(item.id),
  name: item.label,
  children: item.children?.length
    ? item.children.map(choiceToSelectItem)
    : undefined,
})

const findLabelInChoices = (
  items: IFilterChoiceItem[],
  id: string | number,
): string | null => {
  for (const item of items) {
    if (item.id === id) {
      return item.label
    }
    if (item.children?.length) {
      const found = findLabelInChoices(item.children, id)
      if (found) {
        return found
      }
    }
  }

  return null
}

/** Загрузка/поиск/пагинация choices атрибутов и хелперы single/tree-select записи журнала. */
export function useEntryChoicesLoader({
  getJournalId,
  journalAttributes,
  entryForm,
  errors,
  entryLoadedChoiceLabels,
  treeSelectedLabels,
}: UseEntryChoicesLoaderParams) {
  const selectMenu = ref<Record<string, boolean>>({})
  const selectSearches = ref<Record<string, string>>({})
  const nextUrlMap: Record<string, string> = {}

  const filterStore = reactive<IFilterStore>({
    filterChoices: {},
    searchChoices: {},
    filterCursors: {},
    filterLoading: {},
    filterStrategies: {},
    async fetchFilterChoices(fieldKey, search, cursor) {
      const journalId = getJournalId()
      const attrId = Number(fieldKey)
      if (!journalId || Number.isNaN(attrId)) {
        return
      }

      this.filterLoading[fieldKey] = true
      try {
        let data
        if (cursor && nextUrlMap[fieldKey]) {
          data = await journalsApi.getAttributeChoicesByNext(nextUrlMap[fieldKey])
        } else {
          data = await journalsApi.getAttributeChoicesPage(journalId, attrId, false, search || undefined)
        }

        const items = (data.results || []).map(normalizeFilterChoice)

        if (search) {
          this.searchChoices[fieldKey] = cursor
            ? [...(this.searchChoices[fieldKey] || []), ...items]
            : items
        } else {
          this.filterChoices[fieldKey] = cursor
            ? [...(this.filterChoices[fieldKey] || []), ...items]
            : items
        }

        nextUrlMap[fieldKey] = data.next || ''
        this.filterCursors[fieldKey] = data.next || null
      } catch (e) {
        logJournalHttpError(e, {
          function: 'fetchFilterChoices',
          store: 'useEntryChoicesLoader',
          payload: { fieldKey, search, cursor, journalId },
        })
      } finally {
        this.filterLoading[fieldKey] = false
      }
    },
  })

  /** Адаптер IFilterStore → ITreeSelectStore для UniversalTreeMultiSelect. */
  const journalTreeStore: ITreeSelectStore = {
    get treeChoices() {
      return filterStore.filterChoices
    },
    get treeSearchChoices() {
      return filterStore.searchChoices
    },
    get treeCursors() {
      return filterStore.filterCursors
    },
    get treeLoading() {
      return filterStore.filterLoading
    },
    fetchTreeChoices: (fieldKey, search, cursor) =>
      filterStore.fetchFilterChoices(fieldKey, search, cursor),
  }

  const isChoicesLoading = (attr: JournalAttribute): boolean =>
    Boolean(filterStore.filterLoading[getFormKey(attr)])

  const hasMoreChoices = (attr: JournalAttribute): boolean =>
    Boolean(filterStore.filterCursors[getFormKey(attr)])

  const getSelectItems = (attr: JournalAttribute): SelectItem[] => {
    const key = getFormKey(attr)
    const hasSearch = !!selectSearches.value[key]
    const choices = hasSearch
      ? (filterStore.searchChoices[key] || [])
      : (filterStore.filterChoices[key] || [])
    const items = choices.map(choiceToSelectItem)

    const currentVal = entryForm.value[key]
    if (currentVal != null && currentVal !== '') {
      const currentId = String(currentVal)
      if (!items.some(i => i.id === currentId)) {
        const label = entryLoadedChoiceLabels.value[key]
        if (label) {
          items.unshift({ id: currentId, name: label })
        }
      }
    }

    return items
  }

  const onSelectSearch = (attr: JournalAttribute, searchValue: string) => {
    const key = getFormKey(attr)
    selectSearches.value[key] = searchValue
    filterStore.fetchFilterChoices(key, searchValue || undefined)
  }

  const onSelectLoadMore = (attr: JournalAttribute) => {
    const key = getFormKey(attr)
    filterStore.fetchFilterChoices(key, selectSearches.value[key] || undefined, 'cursor')
  }

  const onSelectOpen = (attr: JournalAttribute) => {
    const key = getFormKey(attr)
    if (!filterStore.filterChoices[key]?.length) {
      filterStore.fetchFilterChoices(key)
    }
  }

  const getSingleSelectProp = (
    attr: JournalAttribute,
  ): { value: { id: string } | null } => {
    const val = entryForm.value[getFormKey(attr)]
    if (val == null || val === '') {
      return { value: null }
    }

    return { value: { id: String(val) } }
  }

  const onSingleSelectChange = (
    attr: JournalAttribute,
    selected: { id: string } | null,
  ) => {
    const key = getFormKey(attr)
    if (selected?.id) {
      const numeric = Number(selected.id)
      entryForm.value[key] = (Number.isNaN(numeric) ? selected.id : numeric) as AttributeValueType
    } else {
      entryForm.value[key] = undefined as unknown as AttributeValueType
    }
    if (errors.value[key]) {
      delete errors.value[key]
    }
  }

  const getTreeModelValue = (attr: JournalAttribute): (string | number)[] => {
    const val = entryForm.value[getFormKey(attr)]
    if (Array.isArray(val)) {
      return val as (string | number)[]
    }
    if (val != null && val !== '') {
      return [val as string | number]
    }

    return []
  }

  const onTreeSelectChange = (
    attr: JournalAttribute,
    ids: (string | number)[],
  ) => {
    const key = getFormKey(attr)
    if (isMultiSelect(attr)) {
      entryForm.value[key] = ids as unknown as AttributeValueType
    } else {
      const raw = ids[0]
      const numeric = Number(raw)
      entryForm.value[key] = !Number.isNaN(numeric) && raw !== '' && raw != null
        ? numeric as AttributeValueType
        : (raw ?? '') as AttributeValueType
    }
    if (errors.value[key]) {
      delete errors.value[key]
    }
  }

  const getSelectDisplayValue = (attr: JournalAttribute): string => {
    const key = getFormKey(attr)
    const val = entryForm.value[key]
    const choices = filterStore.filterChoices[key] || []
    const cachedLabels = treeSelectedLabels.value[key] || {}

    const getLabel = (id: string | number): string =>
      findLabelInChoices(choices, id)
      || cachedLabels[id]
      || entryLoadedChoiceLabels.value[key]
      || String(id)

    if (Array.isArray(val)) {
      return (val as (string | number)[]).map(getLabel).filter(Boolean).join(', ')
    }
    if (val != null && val !== '') {
      return getLabel(val as string | number)
    }

    return ''
  }

  /** Предзагружает choices для одиночных select-полей из переданного набора атрибутов. */
  const preloadSingleSelectChoices = (attrIds: number[]) => {
    journalAttributes.value.forEach(attr => {
      if (!attrIds.includes(attr.id)) {
        return
      }
      if (needsChoices(attr) && !isMultiSelect(attr)) {
        const key = getFormKey(attr)
        if (!filterStore.filterChoices[key]?.length) {
          filterStore.fetchFilterChoices(key)
        }
      }
    })
  }

  const resetChoices = () => {
    filterStore.filterChoices = {}
    filterStore.searchChoices = {}
    filterStore.filterCursors = {}
    filterStore.filterLoading = {}
    selectSearches.value = {}
  }

  return {
    getSelectDisplayValue,
    getSelectItems,
    getSingleSelectProp,
    getTreeModelValue,
    hasMoreChoices,
    isChoicesLoading,
    journalTreeStore,
    onSelectLoadMore,
    onSelectOpen,
    onSelectSearch,
    onSingleSelectChange,
    onTreeSelectChange,
    preloadSingleSelectChoices,
    resetChoices,
    selectMenu,
  }
}
